import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Add,
  AttachMoney,
  Phone,
  Email,
  Event as EventIcon,
} from "@mui/icons-material";
import {
  useCompany,
  useIndividuals,
  useOpportunities,
  useCreateOpportunity,
  useBackfillCompanyComms,
  useUpcomingMeetingsForCompany,
  usePastUnloggedMeetingsForCompany,
  type UpcomingMeeting,
} from "@/hooks/useCRM";
import ScheduleMeetingDialog from "@/components/ScheduleMeetingDialog";
import QuickScheduleAuditButton from "@/components/QuickScheduleAuditButton";
import LogMeetingNotesDialog from "@/components/LogMeetingNotesDialog";
import { PIPELINE_LABELS, PIPELINE_COLORS, type PipelineStage } from "@/types";
import apiClient from "@/api/client";
import { brand } from "@/theme";

interface Communication {
  id: string;
  type: string;
  direction: string;
  subject: string | null;
  content: string | null;
  logged_by: string | null;
  occurred_at: string;
}

export default function CRMCompanyDetail() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: company, isLoading, error } = useCompany(companyId);
  const { data: individuals } = useIndividuals(companyId);
  const { data: opportunities } = useOpportunities(companyId);

  const { data: commsData } = useQuery({
    queryKey: ["crm-comms", companyId],
    queryFn: async () => {
      const res = await apiClient.get<{ communications: Communication[] }>(
        `/crm/communications?company_id=${companyId}`,
      );
      return res.data.communications;
    },
    enabled: !!companyId,
  });

  const backfillMutation = useBackfillCompanyComms(companyId ?? "");
  const { data: upcomingMeetings } = useUpcomingMeetingsForCompany(companyId);
  const { data: pastUnloggedMeetings } = usePastUnloggedMeetingsForCompany(companyId);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [logNotesMeeting, setLogNotesMeeting] = useState<UpcomingMeeting | null>(null);
  const [backfillSnackbar, setBackfillSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const runBackfill = async () => {
    if (!companyId) return;
    try {
      const result = await backfillMutation.mutateAsync(365);
      setBackfillSnackbar({
        open: true,
        message:
          result.new > 0
            ? `Logged ${result.new} new comms from Gmail (${result.skipped_dupe} already known)`
            : "No new comms found — already up to date",
        severity: "success",
      });
    } catch (e) {
      // Axios attaches detail on the response — surface it as the message
      type ErrShape = {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      const ax = e as ErrShape;
      const detail = ax.response?.data?.detail ?? ax.message ?? "Backfill failed";
      setBackfillSnackbar({
        open: true,
        message: typeof detail === "string" ? detail : "Backfill failed — see console",
        severity: "error",
      });
    }
  };

  const [oppDialog, setOppDialog] = useState(false);
  const [editingOppId, setEditingOppId] = useState<string | null>(null);
  const [oppForm, setOppForm] = useState({
    title: "",
    pipeline_stage: "lead" as PipelineStage,
    quote_value: "",
    engagement_type: "",
    contract_start_date: "",
    contract_end_date: "",
  });
  const createOpp = useCreateOpportunity();

  // Local "now" formatted for the <input type="datetime-local"> control —
  // YYYY-MM-DDTHH:MM in local time. Pulled into a helper so the form reset
  // (on save AND on each dialog open) doesn't accidentally drift.
  const nowForInput = () => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [commDialog, setCommDialog] = useState(false);
  const [commForm, setCommForm] = useState({
    type: "note",
    direction: "outbound",
    subject: "",
    content: "",
    occurred_at: nowForInput(),
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const commMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      await apiClient.post("/crm/communications", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-comms", companyId] });
      setCommDialog(false);
      setSnackbar({ open: true, message: "Communication logged" });
    },
    // No `onError` — surface via the Alert inside the dialog so Evelina
    // sees what went wrong instead of the dialog silently staying open
    // (which is how the TIMESTAMPTZ-mismatch bug went unnoticed).
  });

  // Reset the form every time the dialog OPENS so it doesn't leak the
  // previous draft / stale timestamp from the last time the page loaded.
  // Closes Evelina's "shows previously entered date and time, not current"
  // bug.
  useEffect(() => {
    if (commDialog) {
      setCommForm({
        type: "note",
        direction: "outbound",
        subject: "",
        content: "",
        occurred_at: nowForInput(),
      });
      commMutation.reset();
    }
  }, [commDialog]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !company) {
    return <Alert severity="error">Company not found.</Alert>;
  }

  const handleSaveOpp = async () => {
    const data: Record<string, unknown> = {
      title: oppForm.title,
      pipeline_stage: oppForm.pipeline_stage,
    };
    if (!editingOppId) data.company_id = companyId;
    data.quote_value = oppForm.quote_value ? parseFloat(oppForm.quote_value) : null;
    data.engagement_type = oppForm.engagement_type || null;
    data.contract_start_date = oppForm.contract_start_date || null;
    data.contract_end_date = oppForm.contract_end_date || null;
    if (editingOppId) {
      await apiClient.put(`/crm/opportunities/${editingOppId}`, data);
      await queryClient.invalidateQueries({ queryKey: ["crm-opportunities", companyId] });
      await queryClient.invalidateQueries({ queryKey: ["crm-pipeline"] });
      await queryClient.invalidateQueries({ queryKey: ["crm-pipeline-value"] });
    } else {
      await createOpp.mutateAsync(data);
    }
    setOppDialog(false);
    setEditingOppId(null);
    setOppForm({
      title: "",
      pipeline_stage: "lead",
      quote_value: "",
      engagement_type: "",
      contract_start_date: "",
      contract_end_date: "",
    });
  };

  const handleEditOpp = (opp: {
    id: string;
    title: string;
    pipeline_stage: PipelineStage;
    quote_value: number | null;
    engagement_type: string | null;
    contract_start_date: string | null;
    contract_end_date: string | null;
  }) => {
    setEditingOppId(opp.id);
    setOppForm({
      title: opp.title,
      pipeline_stage: opp.pipeline_stage,
      quote_value: opp.quote_value != null ? String(opp.quote_value) : "",
      engagement_type: opp.engagement_type ?? "",
      contract_start_date: opp.contract_start_date ?? "",
      contract_end_date: opp.contract_end_date ?? "",
    });
    setOppDialog(true);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/crm")}>
          Back to CRM
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => navigate(`/crm/companies/${companyId}/edit`)}
        >
          Edit
        </Button>
      </Box>

      <Typography variant="h4" sx={{ mb: 0.5 }}>
        {company.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {[company.industry, company.city, company.country].filter(Boolean).join(" · ")}
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              {[
                ["Industry", company.industry],
                ["Size", company.size_band?.replace("_", " ")],
                ["Website", company.website],
                ["Phone", company.phone],
                ["Email", company.email_general],
                [
                  "Address",
                  [company.address_line1, company.city, company.postcode]
                    .filter(Boolean)
                    .join(", "),
                ],
                ["Source", company.source],
                ["Assigned to", company.assigned_to],
              ].map(([label, value]) =>
                value ? (
                  <Box key={label as string} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Box>
                ) : null,
              )}
              {company.tags && company.tags.length > 0 && (
                <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {company.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 0.5 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Upcoming meetings
                </Typography>
                <QuickScheduleAuditButton
                  companyId={companyId}
                  companyName={company?.name}
                  individuals={individuals ?? []}
                  variant="outlined"
                  size="small"
                />
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EventIcon />}
                  onClick={() => setMeetingDialogOpen(true)}
                  title="Schedule a meeting — creates a Google Calendar event AND a linked CRM entry"
                >
                  Schedule
                </Button>
              </Box>
              {upcomingMeetings && upcomingMeetings.length > 0 ? (
                upcomingMeetings.map((m) => {
                  const startsDate = m.starts_at ? new Date(m.starts_at) : null;
                  return (
                    <Box
                      key={m.id}
                      sx={{
                        mb: 1.5,
                        pb: 1.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 0.5, mb: 0.5, alignItems: "center" }}>
                        <Chip label="meeting" size="small" color="primary" variant="outlined" />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                          {startsDate
                            ? startsDate.toLocaleString(undefined, {
                                weekday: "short",
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {m.summary}
                      </Typography>
                      {m.attendee_emails && m.attendee_emails.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          With: {m.attendee_emails.join(", ")}
                        </Typography>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No upcoming meetings scheduled.
                </Typography>
              )}
            </CardContent>
          </Card>

          {pastUnloggedMeetings && pastUnloggedMeetings.length > 0 && (
            <Card sx={{ borderColor: "warning.main", borderWidth: 1, borderStyle: "solid" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="h6" sx={{ flex: 1 }}>
                    Meetings to log ({pastUnloggedMeetings.length})
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Past meetings that haven't had notes logged yet. Click "Log notes" to add a comm
                  and mark them done.
                </Typography>
                {pastUnloggedMeetings.map((m) => {
                  const startsDate = m.starts_at ? new Date(m.starts_at) : null;
                  return (
                    <Box
                      key={m.id}
                      sx={{
                        mb: 1.5,
                        pb: 1.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                          {m.summary}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {startsDate
                            ? startsDate.toLocaleString(undefined, {
                                weekday: "short",
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </Typography>
                      </Box>
                      <Button size="small" variant="outlined" onClick={() => setLogNotesMeeting(m)}>
                        Log notes
                      </Button>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 0.5 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Communications
                </Typography>
                <Button
                  size="small"
                  startIcon={<Email />}
                  onClick={runBackfill}
                  disabled={backfillMutation.isPending}
                  title="Scan your Gmail for past comms with anyone at this company (last 365 days)"
                >
                  {backfillMutation.isPending ? "Scanning…" : "Scan Gmail"}
                </Button>
                <Button size="small" startIcon={<Phone />} onClick={() => setCommDialog(true)}>
                  Log
                </Button>
              </Box>
              {commsData && commsData.length > 0 ? (
                commsData.map((comm) => (
                  <Box
                    key={comm.id}
                    sx={{ mb: 1.5, pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
                  >
                    <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
                      <Chip label={comm.type} size="small" variant="outlined" />
                      <Chip label={comm.direction} size="small" variant="outlined" />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                        {new Date(comm.occurred_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    {comm.subject && (
                      <Typography variant="body2" fontWeight={600}>
                        {comm.subject}
                      </Typography>
                    )}
                    {comm.content && (
                      <Typography variant="body2" color="text.secondary">
                        {comm.content}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No communications logged yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Opportunities
                </Typography>
                <Button size="small" startIcon={<Add />} onClick={() => setOppDialog(true)}>
                  New
                </Button>
              </Box>
              {opportunities && opportunities.length > 0 ? (
                opportunities.map((opp) => (
                  <Box
                    key={opp.id}
                    sx={{
                      mb: 1,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "#fafafa",
                      borderLeft: `3px solid ${PIPELINE_COLORS[opp.pipeline_stage]}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                        {opp.title}
                      </Typography>
                      <Chip
                        label={PIPELINE_LABELS[opp.pipeline_stage]}
                        size="small"
                        sx={{ bgcolor: PIPELINE_COLORS[opp.pipeline_stage], fontWeight: 600 }}
                      />
                      <IconButton
                        size="small"
                        title="Edit opportunity"
                        onClick={() => handleEditOpp(opp)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {opp.quote_value != null &&
                        (() => {
                          const monthly = Number(opp.quote_value);
                          if (
                            opp.engagement_type === "retainer" &&
                            opp.contract_start_date &&
                            opp.contract_end_date
                          ) {
                            const start = new Date(opp.contract_start_date);
                            const end = new Date(opp.contract_end_date);
                            const months =
                              (end.getFullYear() - start.getFullYear()) * 12 +
                              (end.getMonth() - start.getMonth()) +
                              1;
                            const total = monthly * Math.max(months, 1);
                            return (
                              <Chip
                                icon={<AttachMoney />}
                                label={`£${monthly.toLocaleString()}/mo · £${total.toLocaleString()} total`}
                                size="small"
                                variant="outlined"
                              />
                            );
                          }
                          return (
                            <Chip
                              icon={<AttachMoney />}
                              label={`£${monthly.toLocaleString()}${
                                opp.engagement_type === "retainer" ? "/mo" : ""
                              }`}
                              size="small"
                              variant="outlined"
                            />
                          );
                        })()}
                      {opp.engagement_type && (
                        <Chip
                          label={opp.engagement_type.replace("_", " ")}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {opp.contract_start_date && (
                        <Chip
                          label={`Contract: ${opp.contract_start_date}${
                            opp.contract_end_date ? ` → ${opp.contract_end_date}` : ""
                          }`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {opp.likely_start_date && !opp.contract_start_date && (
                        <Chip
                          label={`Likely start: ${opp.likely_start_date}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {opp.total_hours_required != null && (
                        <Chip
                          label={`${opp.total_hours_required}h`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No opportunities yet.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Contacts
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => navigate(`/crm/individuals/new?company_id=${companyId}`)}
                >
                  Add
                </Button>
              </Box>
              {individuals && individuals.length > 0 ? (
                <List disablePadding>
                  {individuals.map((ind) => (
                    <ListItemButton
                      key={ind.id}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                      onClick={() => navigate(`/crm/individuals/${ind.id}/edit`)}
                      title="Edit contact"
                    >
                      <Avatar
                        sx={{
                          bgcolor: brand.forestGreen,
                          width: 32,
                          height: 32,
                          mr: 1.5,
                          fontSize: "0.8rem",
                        }}
                      >
                        {ind.first_name[0]}
                        {ind.last_name[0]}
                      </Avatar>
                      <ListItemText
                        primary={`${ind.first_name} ${ind.last_name}`}
                        secondary={[ind.job_title, ind.email].filter(Boolean).join(" · ")}
                      />
                      {ind.is_primary_contact && (
                        <Chip label="Primary" size="small" color="primary" sx={{ mr: 1 }} />
                      )}
                      <Edit fontSize="small" sx={{ opacity: 0.4 }} />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No contacts yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* New opportunity dialog */}
      <Dialog
        open={oppDialog}
        onClose={() => {
          setOppDialog(false);
          setEditingOppId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingOppId ? "Edit Opportunity" : "New Opportunity"}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}
        >
          <TextField
            label="Title"
            value={oppForm.title}
            onChange={(e) => setOppForm((f) => ({ ...f, title: e.target.value }))}
            fullWidth
            required
          />
          <FormControl fullWidth>
            <InputLabel>Pipeline Stage</InputLabel>
            <Select
              value={oppForm.pipeline_stage}
              label="Pipeline Stage"
              onChange={(e) =>
                setOppForm((f) => ({ ...f, pipeline_stage: e.target.value as PipelineStage }))
              }
            >
              {Object.entries(PIPELINE_LABELS).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={oppForm.engagement_type === "retainer" ? "Monthly Value (£)" : "Quote Value (£)"}
            helperText={
              oppForm.engagement_type === "retainer"
                ? "Monthly amount. Total contract value computed from dates below."
                : undefined
            }
            type="number"
            value={oppForm.quote_value}
            onChange={(e) => setOppForm((f) => ({ ...f, quote_value: e.target.value }))}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Engagement Type</InputLabel>
            <Select
              value={oppForm.engagement_type}
              label="Engagement Type"
              onChange={(e) => setOppForm((f) => ({ ...f, engagement_type: e.target.value }))}
            >
              <MenuItem value="">Not specified</MenuItem>
              <MenuItem value="one_off">One-off</MenuItem>
              <MenuItem value="retainer">Retainer</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Contract Start"
              type="date"
              value={oppForm.contract_start_date}
              onChange={(e) => setOppForm((f) => ({ ...f, contract_start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Contract End"
              type="date"
              value={oppForm.contract_end_date}
              onChange={(e) => setOppForm((f) => ({ ...f, contract_end_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOppDialog(false);
              setEditingOppId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveOpp}
            disabled={!oppForm.title || createOpp.isPending}
          >
            {createOpp.isPending ? "Saving..." : editingOppId ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Log communication dialog */}
      <Dialog open={commDialog} onClose={() => setCommDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Communication</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}
        >
          {commMutation.isError && (
            <Alert severity="error">
              Couldn't save:{" "}
              {(commMutation.error as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail ??
                (commMutation.error as Error)?.message ??
                "unknown error"}
            </Alert>
          )}
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={commForm.type}
              label="Type"
              onChange={(e) => setCommForm((f) => ({ ...f, type: e.target.value }))}
            >
              {["email", "call", "meeting", "note", "linkedin", "other"].map((t) => (
                <MenuItem key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Direction only makes sense for actual two-party comms — hidden
              for notes / future reminders. Closes Evelina's "inbound/
              outbound field not relevant for notes" feedback. */}
          {commForm.type !== "note" && (
            <FormControl fullWidth>
              <InputLabel>Direction</InputLabel>
              <Select
                value={commForm.direction}
                label="Direction"
                onChange={(e) => setCommForm((f) => ({ ...f, direction: e.target.value }))}
              >
                <MenuItem value="inbound">Inbound</MenuItem>
                <MenuItem value="outbound">Outbound</MenuItem>
              </Select>
            </FormControl>
          )}
          <TextField
            label="Subject"
            value={commForm.subject}
            onChange={(e) => setCommForm((f) => ({ ...f, subject: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Notes"
            value={commForm.content}
            onChange={(e) => setCommForm((f) => ({ ...f, content: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            label="When"
            type="datetime-local"
            value={commForm.occurred_at}
            onChange={(e) => setCommForm((f) => ({ ...f, occurred_at: e.target.value }))}
            helperText="Past = log historical; future = mark as scheduled. Defaults to now."
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() =>
              commMutation.mutate({
                company_id: companyId,
                ...commForm,
                // Direction is always outbound for notes — the field is hidden
                // but the backend requires a value.
                direction: commForm.type === "note" ? "outbound" : commForm.direction,
                // Convert datetime-local to ISO with seconds + Z; the backend
                // accepts any ISO datetime and will store it as occurred_at.
                occurred_at: commForm.occurred_at
                  ? new Date(commForm.occurred_at).toISOString()
                  : undefined,
              })
            }
            disabled={commMutation.isPending}
          >
            {commMutation.isPending ? "Saving..." : "Log"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />

      <Snackbar
        open={backfillSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setBackfillSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={backfillSnackbar.severity} variant="filled">
          {backfillSnackbar.message}
        </Alert>
      </Snackbar>

      <ScheduleMeetingDialog
        open={meetingDialogOpen}
        onClose={() => setMeetingDialogOpen(false)}
        companyId={companyId}
        individuals={individuals ?? []}
        onScheduled={(result) => {
          setBackfillSnackbar({
            open: true,
            message: result.html_link
              ? "Meeting scheduled — invites sent + added to your calendar"
              : "Meeting scheduled",
            severity: "success",
          });
        }}
      />

      <LogMeetingNotesDialog
        open={!!logNotesMeeting}
        onClose={() => setLogNotesMeeting(null)}
        meeting={logNotesMeeting}
        companyId={companyId}
      />
    </Box>
  );
}
